import { useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

export const CONTROL_CENTER_PATH = '/control-center';

export function isControlCenterPath(pathname: string): boolean {
  return pathname === CONTROL_CENTER_PATH || pathname.startsWith(`${CONTROL_CENTER_PATH}/`);
}

/**
 * SEE-265: tell a support user they've switched into the Control Center view.
 *
 * Fires once per entry — when the path moves from outside /control-center to inside
 * it. Moving between Control Center tabs doesn't re-fire, and neither does landing
 * on /control-center directly (reload / deep link): the ticket ties the snackbar to
 * clicking the Control Center option.
 */
export function useControlCenterEntrySnackbar(): void {
  const { pathname } = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const previousPathname = useRef<string | null>(null);

  useEffect(() => {
    const previous = previousPathname.current;
    previousPathname.current = pathname;

    if (previous !== null && !isControlCenterPath(previous) && isControlCenterPath(pathname)) {
      enqueueSnackbar(t('controlCenterSwitched', 'You have switched to the Control Center view.'), {
        variant: 'info',
      });
    }
  }, [pathname, enqueueSnackbar, t]);
}
